import { useEffect, useState, type FC } from 'react';

import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import { ImageOff } from 'lucide';

import Icon from 'common/components/Icon';

interface Props {
  /** URL ảnh đã hoàn chỉnh (caller tự ghép với storage KIOS nếu API trả path tương đối). */
  src?: string | null;
  alt: string;
  /** sx cho thẻ img và cho ô giữ chỗ — dùng chung để khung không nhảy kích thước khi ảnh lỗi. */
  sx?: SxProps<Theme>;
  /** Cỡ icon giữ chỗ; để trống thì theo cỡ mặc định của icon. */
  fallbackIconSize?: number;
}

const DEV_STORAGE_HOST = 'kios-dev.huongvietsinh.com';
const PROD_STORAGE_HOST = 'kios.huongvietsinh.com';

/**
 * File UUID trên kios-dev hay 404 dù cùng path vẫn còn trên storage production
 * (vd ảnh kênh Bữa chính / Bữa phụ / Căng tin). Thử lại đúng path trên host production.
 */
const productionStorageSrc = (src: string): string | null => {
  if (!src.includes(DEV_STORAGE_HOST) || !src.includes('/images/')) return null;
  const next = src.replace(DEV_STORAGE_HOST, PROD_STORAGE_HOST);
  return next === src ? null : next;
};

/**
 * Ảnh lấy từ storage KIOS. Storage này có những file trả 503/404 (dữ liệu KIOS thiếu file — không
 * phải ghép URL sai, vì ảnh khác trong cùng thư mục vẫn tải được), khi đó `<img>` vỡ và trình
 * duyệt hiện chữ alt trơ ra giữa bảng/thẻ. Component này rơi về icon "không có ảnh" cho gọn.
 * Đây là che triệu chứng: ảnh vẫn phải do phía KIOS bù.
 */
const KiosImage: FC<Props> = ({ src, alt, sx, fallbackIconSize = 24 }) => {
  const [failed, setFailed] = useState(false);
  const [useProduction, setUseProduction] = useState(false);

  // Ảnh đổi (đổi trang, đổi bộ lọc) thì cho phép thử tải lại, không giữ mãi trạng thái lỗi cũ.
  useEffect(() => {
    setFailed(false);
    setUseProduction(false);
  }, [src]);

  const productionSrc = src ? productionStorageSrc(src) : null;
  const currentSrc = useProduction && productionSrc ? productionSrc : src;

  if (!currentSrc || failed) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'action.hover',
          color: 'text.disabled',
          ...sx,
        }}
      >
        <Icon icon={ImageOff} size={fallbackIconSize} />
      </Box>
    );
  }

  return (
    <Box
      component="img"
      src={currentSrc}
      alt={alt}
      onError={() => {
        if (!useProduction && productionSrc) setUseProduction(true);
        else setFailed(true);
      }}
      sx={sx}
    />
  );
};

export default KiosImage;
